import {
  useMutation,
  type UseMutationResult,
  useQueryClient,
} from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import {
  startScopedCase,
  type StartScopedCaseError,
} from "@/api/endpoints/start-scoped-case";
import { qk } from "@/api/query-keys";
import { useAuth } from "@/auth/use-auth";
import type { MessageStart } from "@/domain/process-model/message-start";
import { notifySuccess } from "@/lib/notify";

export type StartScopedInput = {
  readonly scoped: MessageStart;
  readonly businessData: Readonly<Record<string, unknown>>;
};

/**
 * 部署スコープ版の起票。
 *
 * 承認起票 leaf に投げる（承認者の解決と凍結はサーバ側）。leaf は渡したトークンを
 * 申請者の委任トークンとして SpiffArena の起票に使うので、`Authorization` に
 * 載せている access_token をそのまま渡す。
 *
 * 口は**同一オリジン**（`/approval/start`）。本番は CloudFront の `/approval/*`
 * behavior が leaf へ転送し、dev は vite proxy が同形を再現する。
 */
export function useStartScopedApplication(
  options: {
    /** leaf のオリジン。既定は同一オリジン（相対パス）。テストの差し替え用。 */
    readonly baseUrl?: string;
    /** テスト用の差し替え（api client と同じ作法）。 */
    readonly fetchImpl?: typeof fetch;
  } = {},
): UseMutationResult<
  { readonly caseId: number },
  StartScopedCaseError,
  StartScopedInput
> {
  const auth = useAuth();
  const queryClient = useQueryClient();
  const { t } = useTranslation("common");
  // 同一オリジン既定（本番は CloudFront の /approval/*、dev は vite proxy）
  const baseUrl = options.baseUrl ?? "";

  return useMutation({
    mutationFn: ({ scoped, businessData }: StartScopedInput) =>
      startScopedCase(
        {
          messageName: scoped.messageName,
          lanes: scoped.approvalLanes,
          businessData,
        },
        {
          baseUrl,
          accessToken: bearerOf(auth.getAuthHeaders()),
          ...(options.fetchImpl ? { fetchImpl: options.fetchImpl } : {}),
        },
      ),
    onSuccess: async () => {
      // 成功すると即マイ案件へ戻るので、通ったことがどこにも残らない
      notifySuccess(t("toast.applicationStarted"));

      // 一覧と要対応の件数が変わる
      await queryClient.invalidateQueries({
        queryKey: qk.processInstances._root(),
      });
    },
  });
}

/** Authorization ヘッダから素のトークンを取り出す（無ければ空＝leaf が 401 を返す）。 */
function bearerOf(headers: Record<string, string>): string {
  const value = headers.Authorization ?? headers.authorization ?? "";
  return value.startsWith("Bearer ") ? value.slice(7) : "";
}
